import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { CustomXmlPart } from '../interfaces/customxmlpart';
import { AsyncResult } from '../utils/asyncresult';
import { IFunction } from './functions/ifunction';
import { LoggingService } from './logging.service';
import { WordService } from './word.service';

export interface INamespaceSettings {
  name: string;
  modelId: string;
  dopeXPathsId: string;
  dopeConditionsId: string;
  jsonModel: any;
  functions?: IFunction[];
}

export interface INamespacePart {
  name: string;
  model: CustomXmlPart;
  dopeXPaths: CustomXmlPart;
  dopeConditions: CustomXmlPart;
}

export type AddInInfo = {
  createDate: number;
  createVersion: string;
  currentVersion: string;
};

@Injectable({ providedIn: 'root' })
export class ApplicationStoreService {
  private static NAMESPACE_SETTINGS_KEY = 'kp-ns-parts';
  private static NEXT_REF_ID_KEY = 'kp-next-ref-id';
  private static ADD_IN_INFO_KEY = 'kp-add-in-info';
  private static NAMESPACE_ENTITIES_KEY = 'kp-ns-entity-list';

  constructor(private wordService: WordService, private loggingService: LoggingService) {
    // order of these is important
    this.initialiseInfo();
    this.initialiseNextRefId();
  }

  /**
   * Increments the global reference id
   * @return the id **before** it was incremented
   */
  incrementRefId(): Promise<number> {
    return this.incrementId(ApplicationStoreService.NEXT_REF_ID_KEY);
  }

  getNamespaces(): string[] {
    const allSettings = ApplicationStoreService.getNamespaceSettingList();
    return allSettings.map((nsSetting) => nsSetting.name);
  }

  hasNamespaceSetting(namespace: string): boolean {
    return this.getNamespaceSettings(namespace) != null;
  }

  getNamespaceSettings(namespace: string): INamespaceSettings | null {
    const allSettings = ApplicationStoreService.getNamespaceSettingList();

    const nsSettings = allSettings.find((nsp) => nsp.name === namespace);
    if (!nsSettings) {
      this.loggingService.debug(
        'ApplicationStoreService',
        'getNamespaceSettings',
        `Settings for '${namespace}' not found. ${allSettings.length} available.`
      );
      return null;
    }

    return nsSettings;
  }

  async setNamespaceSettings(namespace: string, settings: INamespaceSettings): Promise<void> {
    const settingsList = ApplicationStoreService.getNamespaceSettingList();
    const insertPos = settingsList.findIndex((s) => s.name === namespace);
    const deleteCount = insertPos === -1 ? 0 : 1;
    settingsList.splice(insertPos, deleteCount, settings);
    return ApplicationStoreService.setNamespaceSettingList(settingsList);
  }

  hasNamespaceEntities(namespace: string): boolean {
    return this.getNamespaceEntities(namespace) !== undefined;
  }

  getNamespaceEntities(namespace: string): string[] | undefined {
    const list = this.get(ApplicationStoreService.NAMESPACE_ENTITIES_KEY);
    return list ? NamespaceEntitiesManager.build(list).get(namespace) : undefined;
  }

  async putNamespaceEntity(namespace: string, entity: string): Promise<void> {
    const list = this.get(ApplicationStoreService.NAMESPACE_ENTITIES_KEY);
    const neMgr = NamespaceEntitiesManager.build(list ?? []);
    neMgr.put(namespace, entity);
    return this.set(ApplicationStoreService.NAMESPACE_ENTITIES_KEY, neMgr.list());
  }

  getAddInInfo(): AddInInfo {
    return Office.context.document.settings.get(ApplicationStoreService.ADD_IN_INFO_KEY);
  }

  get(key: string): any {
    return Office.context.document.settings.get(key);
  }

  set(key: string, value: any): Promise<void> {
    return new Promise((resolve, reject) => {
      Office.context.document.settings.set(key, value);
      Office.context.document.settings.saveAsync((result) => {
        if (AsyncResult.succeeded(result)) {
          resolve();
        } else {
          reject(result.error);
        }
      });
    });
  }

  async getNamespacePart(nsSettings: INamespaceSettings): Promise<INamespacePart> {
    const model = await this.wordService.getCustomXmlPart(nsSettings.modelId);
    const dopeXPaths = await this.wordService.getCustomXmlPart(nsSettings.dopeXPathsId);
    const dopeConditions = await this.wordService.getCustomXmlPart(nsSettings.dopeConditionsId);
    return {
      name: nsSettings.name,
      model: model,
      dopeXPaths: dopeXPaths,
      dopeConditions: dopeConditions,
    };
  }

  private initialiseNextRefId(): void {
    const nextRefId = Office.context.document.settings.get(ApplicationStoreService.NEXT_REF_ID_KEY);
    if (!nextRefId) {
      Office.context.document.settings.set(ApplicationStoreService.NEXT_REF_ID_KEY, 1);
      Office.context.document.settings.saveAsync();
    }
  }

  private incrementId(key: string): Promise<number> {
    const refId = Office.context.document.settings.get(key);
    return new Promise((resolve, reject) => {
      Office.context.document.settings.set(key, refId + 1);
      Office.context.document.settings.saveAsync((result) => {
        if (AsyncResult.succeeded(result)) {
          resolve(refId);
        } else {
          reject(result.error);
        }
      });
    });
  }

  private static getNamespaceSettingList(): INamespaceSettings[] {
    return Office.context.document.settings.get(ApplicationStoreService.NAMESPACE_SETTINGS_KEY) || [];
  }

  private static setNamespaceSettingList(settings: INamespaceSettings[]): Promise<void> {
    return new Promise((resolve, reject) => {
      Office.context.document.settings.set(ApplicationStoreService.NAMESPACE_SETTINGS_KEY, settings);
      Office.context.document.settings.saveAsync((result) => {
        if (!AsyncResult.succeeded(result)) {
          const msg = `Failed to save namespace settings. ${result.error}`;
          console.error(msg);
          reject(msg);
        } else {
          resolve();
        }
      });
    });
  }

  private initialiseInfo(): void {
    const brandNew = Office.context.document.settings.get(ApplicationStoreService.NEXT_REF_ID_KEY) === null;
    if (brandNew) {
      const info = {
        createDate: Date.now(),
        createVersion: environment.version,
        currentVersion: environment.version,
      };
      Office.context.document.settings.set(ApplicationStoreService.ADD_IN_INFO_KEY, info);
      Office.context.document.settings.saveAsync();
    }
  }
}

/**
 * Helper class for maintaining the list of entities *used* in the template for each namespace.
 */
class NamespaceEntitiesManager {
  private readonly namespaceToEntitiesList: {
    namespace: string;
    entities: string[];
  }[] = [];

  private constructor(list: { namespace: string; entities: string[] }[]) {
    this.namespaceToEntitiesList = list;
  }

  static build(list: { namespace: string; entities: string[] }[]): NamespaceEntitiesManager {
    return new NamespaceEntitiesManager(list);
  }

  get(namespace: string): string[] | undefined {
    const entry = this.namespaceToEntitiesList.find((e) => e.namespace === namespace);
    return entry?.entities;
  }

  put(namespace: string, entity: string): void {
    const entry = this.namespaceToEntitiesList.find((e) => e.namespace === namespace);
    if (!entry) {
      this.namespaceToEntitiesList.push({
        namespace: namespace,
        entities: [entity],
      });
      return;
    }

    const exists = entry.entities.find((e) => e === entity) !== undefined;
    if (!exists) {
      entry.entities.push(entity);
    }
  }

  list(): Readonly<{ namespace: string; entities: string[] }[]> {
    return this.namespaceToEntitiesList;
  }
}
